import { toast } from 'sonner';

import { useCaptureError } from '@/lib/posthog/hooks/useCaptureError';
import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { useConnectSlack } from '@/modules/integrations/hooks/useConnectSlack';
import { useUpdateSignupFlowMutation } from '@/modules/signup-flow/mutations/useUpdateSignupFlowMutation';
import { signupFlowStep } from '@/modules/signup-flow/signupFlowSteps';
import { useIsSlackConnected } from '@/modules/user-external-accounts/hooks/useIsSlackConnected';
import { useUserExternalAccountsQuery } from '@/modules/user-external-accounts/queries/useUserExternalAccountsQuery';

import { SlackChannelStep } from './SlackChannelStep';
import { SlackConnect } from './SlackConnect';

/**
 * Signup Slack step. Before OAuth it offers to add Finlay to Slack; once the
 * user's Slack account is connected (OAuth finishes in a new tab) it asks
 * which channel Finlay should join.
 */
export const SlackIntegration = () => {
  const captureError = useCaptureError();
  const trackEvent = useTrackEvent();

  const connectSlack = useConnectSlack();
  const { userId, isSlackConnected } = useIsSlackConnected();
  // OAuth runs in another tab: refetch on return so the channel picker shows
  // as soon as Slack connects, even within the default stale time.
  useUserExternalAccountsQuery(userId, { refetchOnWindowFocus: 'always' });
  const updateSignupFlowMutation = useUpdateSignupFlowMutation();

  const handleBack = () => {
    updateSignupFlowMutation.mutate(
      { step: signupFlowStep.companyGoals },
      {
        onError: () => {
          toast.error('Failed to go back. Please try again.');
        },
      },
    );
  };

  const handleSkip = () => {
    updateSignupFlowMutation.mutate(
      { step: signupFlowStep.summary },
      {
        onError: (error) => {
          captureError(error, {
            errorType: 'slack-integration-signup-flow-error',
          });
          toast.error(
            'Failed to continue to the next step. Please try again or contact support.',
          );
        },
        onSuccess: () => {
          trackEvent('slackIntegrationFinished');
          trackEvent('slackIntegrationSkipped');
        },
      },
    );
  };

  if (isSlackConnected) {
    return <SlackChannelStep onBack={handleBack} />;
  }

  return (
    <SlackConnect
      isSubmitting={updateSignupFlowMutation.isPending}
      onBack={handleBack}
      onConnect={() => connectSlack('SignUp', { newTab: true })}
      onSkip={handleSkip}
    />
  );
};
